import React, { useState, useEffect, useCallback, useImperativeHandle, ReactNode, useRef, forwardRef } from "react";
import { Select, Input, Button, Alert, Tooltip, Typography, Spin, Modal, Collapse } from "antd";
import axios from "axios";
import { Scanner } from "@yudiel/react-qr-scanner";
import { useHistory, useParams } from "react-router-dom";
import { IsOpenType, RegistrationWithStudent } from "@bindeleddet/core/types";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  SwapOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const { Option } = Select;

interface Event {
  id: number;
  name: string;
  start: string;
  is_open: number;
}

interface AttendanceResponse {
  success?: string;
  error?: string;
  student?: {
    username: string;
    full_name?: string | null;
  };
}

type AlertType = "info" | "success" | "warning" | "error";
type AlertMessage = string | ReactNode;

interface CameraDevice {
  deviceId: string;
  label: string;
  facingMode?: "user" | "environment";
}

interface AttendanceStudent {
  username: string;
  name: string;
  attended: boolean;
  yearLevel: number;
  studyProgramme: string;
  mobileNumber: number | null;
}

const StudentContactRow: React.FC<{
  student: AttendanceStudent;
  showStatus?: boolean;
  showUsername?: boolean;
  noWrap?: boolean;
}> = ({ student, showStatus = false, showUsername = true, noWrap = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "8px 0",
      borderBottom: "1px solid #f0f0f0",
      textAlign: "left",
      whiteSpace: noWrap ? "nowrap" : undefined,
    }}
  >
    {showStatus &&
      (student.attended ? (
        <CheckCircleFilled style={{ color: "#52c41a", fontSize: "18px", flexShrink: 0 }} />
      ) : (
        <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: "18px", flexShrink: 0 }} />
      ))}
    <div style={{ flex: 1, minWidth: 0 }}>
      <Text strong>{student.name}</Text>
      {showUsername && (
        <Text type="secondary" style={{ marginLeft: "8px" }}>
          {student.username}
        </Text>
      )}
    </div>
    {student.mobileNumber ? (
      <a href={`tel:${student.mobileNumber}`} style={{ flexShrink: 0 }}>
        {student.mobileNumber}
      </a>
    ) : (
      <Text type="secondary" style={{ flexShrink: 0 }}>
        Mangler nummer
      </Text>
    )}
  </div>
);

export interface AttendanceOverviewHandle {
  markAttended: (username: string) => void;
}

interface AttendanceOverviewProps {
  eventId: number | null;
  // Rendered between the attendance counter and the student lists
  children?: ReactNode;
}

const AttendanceOverview = forwardRef<AttendanceOverviewHandle, AttendanceOverviewProps>(function AttendanceOverview(
  { eventId, children },
  ref
) {
  const [students, setStudents] = useState<AttendanceStudent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(false);
    try {
      const { data } = await axios.get<RegistrationWithStudent[]>(`/events/${eventId}/registrations-with-students/`);

      setStudents(
        data.map(reg => ({
          username: reg.username,
          name: `${reg.first_name} ${reg.last_name}`,
          attended: !!reg.time_attended,
          yearLevel: reg.year_level,
          studyProgramme: reg.study_programme_shortname,
          mobileNumber: reg.mobile_number,
        }))
      );
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useImperativeHandle(
    ref,
    () => ({
      markAttended: (username: string) => {
        const found = students.some(s => s.username === username);
        if (!found) {
          // Student not in list - trigger full refresh
          fetchData();
          return;
        }
        setStudents(prev => prev.map(s => (s.username === username ? { ...s, attended: true } : s)));
      },
    }),
    [fetchData, students]
  );

  const attended = students.filter(s => s.attended).length;
  const total = students.length;

  const sorted = [...students].sort((a, b) => {
    if (a.attended === b.attended) return a.name.localeCompare(b.name);
    return a.attended ? 1 : -1;
  });

  const byName = [...students].sort((a, b) => a.name.localeCompare(b.name));
  const notAttended = byName.filter(s => !s.attended);

  const listStyle: React.CSSProperties = { maxHeight: "40vh", overflowY: "auto" };

  if (!eventId) return <>{children}</>;

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          margin: "8px 0",
        }}
      >
        <Button
          onClick={() => setModalOpen(true)}
          size="small"
          aria-label="Vis oversikt over oppmøte"
          style={{
            padding: "4px 12px",
            height: "auto",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {loading ? (
            <>
              <Spin size="small" />
              <Text type="secondary" style={{ fontSize: "13px" }}>
                {" "}
                Henter oppmøte...
              </Text>
            </>
          ) : error ? (
            <Text type="danger" style={{ fontSize: "13px" }}>
              Kunne ikke hente oppmøte
            </Text>
          ) : (
            <>
              <Text strong style={{ fontSize: "14px" }}>
                {attended} / {total}
              </Text>
              <Text type="secondary" style={{ fontSize: "13px" }}>
                {" "}
                oppmøtt
              </Text>
            </>
          )}
        </Button>
        <Button
          icon={<ReloadOutlined spin={loading} />}
          onClick={fetchData}
          disabled={loading}
          size="small"
          aria-label="Last inn oppmøte på nytt"
        />

        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>
                Oversikt ({attended} / {total})
              </span>
              <Button
                icon={<ReloadOutlined spin={loading} />}
                onClick={fetchData}
                disabled={loading}
                size="small"
                aria-label="Last inn oppmøte på nytt"
                style={{ marginRight: "24px" }}
              />
            </div>
          }
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          footer={null}
          bodyStyle={{ padding: "8px 16px", overflowY: "auto" }}
          style={{ top: 20, maxWidth: "500px" }}
          width="95vw"
          centered={false}
        >
          <div style={{ maxHeight: "calc(90vh - 100px)", overflowY: "auto" }}>
            {sorted.map(student => (
              <div
                key={`${student.username}-${student.name}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "8px 0",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                {student.attended ? (
                  <CheckCircleFilled style={{ color: "#52c41a", fontSize: "18px", flexShrink: 0 }} />
                ) : (
                  <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: "18px", flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong>{student.name}</Text>
                  {(student.yearLevel || student.studyProgramme) && (
                    <Text type="secondary" style={{ marginLeft: "8px" }}>
                      {[student.studyProgramme, student.yearLevel ? `${student.yearLevel}. klasse` : ""]
                        .filter(Boolean)
                        .join(", ")}
                    </Text>
                  )}
                </div>
              </div>
            ))}
            {error && <Text type="danger">Kunne ikke hente oppmøte. Prøv igjen.</Text>}
            {!error && students.length === 0 && <Text type="secondary">Ingen påmeldte studenter</Text>}
          </div>
        </Modal>
      </div>

      {children}

      <Collapse
        style={{ margin: "8px 0", textAlign: "left" }}
        items={[
          {
            key: "not-attended",
            label: `Ikke innsluppet (${notAttended.length})`,
            children: (
              <div style={listStyle}>
                {notAttended.map(student => (
                  <StudentContactRow key={student.username} student={student} showUsername={false} />
                ))}
                {!error && students.length > 0 && notAttended.length === 0 && (
                  <Text type="secondary">Alle er sluppet inn 🎉</Text>
                )}
              </div>
            ),
          },
          {
            key: "all",
            label: `Alle påmeldte (${total})`,
            children: (
              <div style={{ ...listStyle, overflowX: "auto" }}>
                <div style={{ minWidth: "max-content" }}>
                  {byName.map(student => (
                    <StudentContactRow key={student.username} student={student} showStatus noWrap />
                  ))}
                </div>
                {!error && students.length === 0 && <Text type="secondary">Ingen påmeldte studenter</Text>}
              </div>
            ),
          },
        ]}
      />
    </>
  );
});

const ManualAttendanceInput: React.FC<{
  disabled: boolean;
  onRegister: (username: string) => void;
  onEmptyUsername: () => void;
}> = ({ disabled, onRegister, onEmptyUsername }) => {
  const [username, setUsername] = useState("");

  const handleSubmit = () => {
    if (!username) {
      onEmptyUsername();
      return;
    }
    onRegister(username);
    setUsername("");
  };

  return (
    <div style={{ width: "100%", margin: "8px auto" }}>
      <Input
        placeholder="Skriv inn brukernavn"
        aria-label="Skriv inn brukernavn"
        style={{ marginBottom: "8px", width: "100%" }}
        value={username}
        onChange={e => setUsername(e.target.value)}
        disabled={disabled}
        onPressEnter={handleSubmit}
      />
      <Button type="primary" onClick={handleSubmit} block aria-label="Registrer oppmøte" disabled={disabled}>
        Registrer Oppmøte
      </Button>
    </div>
  );
};

const TakeAttendanceForEvent: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<number | null>(null);
  const attendanceRef = useRef<AttendanceOverviewHandle>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [loadingScan, setLoadingScan] = useState(false);
  const [availableCameras, setAvailableCameras] = useState<CameraDevice[]>([]);
  const [selectedCameraIndex, setSelectedCameraIndex] = useState<number>(0);
  const [isFrontCamera, setIsFrontCamera] = useState<boolean>(true);
  const [cameraSwitchKey, setCameraSwitchKey] = useState(0);
  const [lastScannedCode, setLastScannedCode] = useState<string>("");
  const [finderColor, setFinderColor] = useState<string>("var(--primary-color)");
  const lastScannedClearTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { id } = useParams<{ id: string }>();

  const history = useHistory();

  const [isAlertVisible, setIsAlertVisible] = useState(true);
  const [alertInfo, setAlertInfo] = useState<{
    type: AlertType;
    message: AlertMessage;
  }>({
    type: "info",
    message: "Begynn å ta oppmøte!",
  });

  useEffect(() => {
    if (id) {
      axios
        .get(`/events/admin-events/${id}/`)
        .then(response => {
          setEvents([response.data]);
          setSelectedEvent(response.data.id);
        })
        .catch(() => {
          history.push("/admin/tools/take-attendance-for-event-v2/");
        });
    } else {
      axios
        .get("/events/future-events/")
        .then(response => {
          const filteredEvents = response.data.filter((event: Event) => event.is_open === IsOpenType.CLOSED);
          setEvents(filteredEvents);
        })
        .catch(() => {
          setAlertInfo({
            type: "error",
            message: "Kunne ikke hente arrangementer. Prøv igjen.",
          });
        });
    }
  }, [id, history]);

  useEffect(() => {
    const enumerateCameras = async () => {
      try {
        // Request permission first
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });

        // Stop the stream immediately after getting permission
        stream.getTracks().forEach(track => track.stop());

        const devices = await navigator.mediaDevices.enumerateDevices();
        const allVideoDevices = devices.filter(device => device.kind === "videoinput");

        // Get facingMode for each device by requesting a stream
        const devicesWithFacingMode = await Promise.all(
          allVideoDevices.map(async device => {
            try {
              const deviceStream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: device.deviceId } },
              });
              const track = deviceStream.getVideoTracks()[0];
              const settings = track.getSettings();
              const { facingMode } = settings;

              // Stop the stream immediately
              deviceStream.getTracks().forEach(t => t.stop());

              return {
                deviceId: device.deviceId,
                label: device.label || `Kamera`,
                facingMode: facingMode as "user" | "environment" | undefined,
              };
            } catch {
              // If we can't access the device, skip it
              return null;
            }
          })
        );

        // Filter to only include devices with facingMode (front/back cameras)
        // and remove duplicates (keep only one front and one back)
        const frontCameras = devicesWithFacingMode.filter(d => d && d.facingMode === "user");
        const backCameras = devicesWithFacingMode.filter(d => d && d.facingMode === "environment");

        const videoDevices: CameraDevice[] = [];
        if (frontCameras.length > 0 && frontCameras[0]) {
          // Use the first front camera
          videoDevices.push(frontCameras[0]);
        }
        if (backCameras.length > 0 && backCameras[0]) {
          // Use the first back camera
          videoDevices.push(backCameras[0]);
        }

        setAvailableCameras(videoDevices);

        // Set initial camera type - default to first camera, assume front if unknown
        if (videoDevices.length > 0) {
          const firstCamera = videoDevices[0];
          setIsFrontCamera(firstCamera.facingMode === "user" || firstCamera.facingMode === undefined);
        }
      } catch {
        // Silently fail if camera enumeration fails
      }
    };

    enumerateCameras();
  }, []);

  // Make camera video responsive, detect camera type, and handle camera switching
  useEffect(() => {
    if (isScanning && availableCameras.length > 0) {
      let switchAttempted = false;

      const switchToSelectedCamera = () => {
        const video = document.querySelector("video");
        if (!video) return;

        const currentCamera = availableCameras[selectedCameraIndex];
        if (!currentCamera) return;

        const stream = video.srcObject as MediaStream;
        if (!stream) return;

        const track = stream.getVideoTracks()[0];
        if (!track) return;

        const { deviceId: currentDeviceId } = track.getSettings();

        // Only switch if device IDs don't match
        if (currentDeviceId && currentDeviceId !== currentCamera.deviceId && !switchAttempted) {
          switchAttempted = true;

          // Stop current track
          track.stop();

          // Get new stream with selected camera
          const constraints: MediaStreamConstraints = {
            video: currentCamera.deviceId
              ? { deviceId: { exact: currentCamera.deviceId } }
              : currentCamera.facingMode
              ? { facingMode: currentCamera.facingMode }
              : true,
          };

          navigator.mediaDevices
            .getUserMedia(constraints)
            .then(newStream => {
              video.srcObject = newStream;
              setIsFrontCamera(currentCamera.facingMode === "user" || currentCamera.facingMode === undefined);
              switchAttempted = false;
            })
            .catch(() => {
              switchAttempted = false;
            });
        }
      };

      // Try to switch immediately and then periodically
      const timeoutId = setTimeout(switchToSelectedCamera, 300);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isScanning, selectedCameraIndex, availableCameras]);

  const showAlert = (alertType: AlertType, msg: AlertMessage) => {
    setAlertInfo({ type: alertType, message: msg });
    setIsAlertVisible(true);

    // Update finder color based on alert type
    let color = "var(--primary-color)";
    if (alertType === "error") {
      color = "#ff4d4f"; // Red
    } else if (alertType === "warning") {
      color = "#faad14"; // Yellow/Orange
    } else if (alertType === "success") {
      color = "#52c41a"; // Green
    }

    setFinderColor(color);

    // Reset to default color after 1 second
    setTimeout(() => {
      setFinderColor("var(--primary-color)");
    }, 1000);
  };

  const renderMessage = (message: AlertMessage): ReactNode => {
    if (typeof message === "string") {
      return <Text style={{ color: "var(--dark)" }}>{message}</Text>;
    }
    return message;
  };

  const scheduleLastScannedReset = (code: string) => {
    if (lastScannedClearTimeout.current) {
      clearTimeout(lastScannedClearTimeout.current);
    }
    lastScannedClearTimeout.current = setTimeout(() => {
      setLastScannedCode(current => (current === code ? "" : current));
    }, 2000);
  };

  const getStudentDisplayName = (data: AttendanceResponse | undefined, fallbackUsername: string) => {
    const fullName = data?.student?.full_name?.trim();
    const usernameFromResponse = data?.student?.username;

    if (fullName) {
      return fullName;
    }

    return usernameFromResponse || fallbackUsername;
  };

  const registerAttendance = async (inputUsername: string) => {
    if (!selectedEvent) {
      showAlert("error", <Text>Du må velge arrangementet før du kan registrere oppmøte.</Text>);
      return;
    }

    setLoadingScan(true);
    setIsAlertVisible(false);
    try {
      const response = await axios.post<AttendanceResponse>(`/events/${selectedEvent}/register-attendance/`, {
        username: inputUsername,
      });
      const displayName = getStudentDisplayName(response.data, inputUsername);

      showAlert(
        "success",
        <Text style={{ color: "var(--dark)" }}>
          Oppmøte registrert for:{" "}
          <Text strong style={{ color: "var(--dark)" }}>
            {displayName}
          </Text>
        </Text>
      );
      attendanceRef.current?.markAttended(inputUsername);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data as AttendanceResponse | undefined;
        const errorMsg = errorData?.error || "Kunne ikke registrere oppmøte, prøv igjen.";
        const displayName = getStudentDisplayName(errorData, inputUsername);
        let alertType: "error" | "warning" = "error";
        let finalMsg: ReactNode = (
          <Text style={{ color: "var(--dark)" }}>
            Kunne ikke registrere oppmøte for{" "}
            <Text strong style={{ color: "var(--dark)" }}>
              {displayName}
            </Text>
            . Prøv igjen.
          </Text>
        );

        if (errorMsg === "Username is required.") {
          finalMsg = "Brukernavn er påkrevd.";
        } else if (errorMsg === "Student do not exist.") {
          finalMsg = "Studenten finnes ikke.";
        } else if (errorMsg === "Event not found.") {
          finalMsg = "Arrangementet ble ikke funnet.";
        } else if (errorMsg === "Student is not registered for this event.") {
          finalMsg = (
            <Text style={{ color: "var(--dark)" }}>
              Studenten{" "}
              <Text strong style={{ color: "var(--dark)" }}>
                {displayName}
              </Text>{" "}
              er ikke registrert for dette arrangementet.
            </Text>
          );
        } else if (errorMsg === "Attendance has already been recorded.") {
          finalMsg = (
            <Text style={{ color: "var(--dark)" }}>
              Oppmøte er allerede registrert for:{" "}
              <Text strong style={{ color: "var(--dark)" }}>
                {displayName}
              </Text>
            </Text>
          );
          alertType = "warning";
        }

        showAlert(alertType, finalMsg);
      } else {
        showAlert(
          "error",
          <Text>
            Ukjent feil ved registrering av <Text strong>{inputUsername}</Text>. Prøv igjen.
          </Text>
        );
      }
    } finally {
      setLoadingScan(false);
      scheduleLastScannedReset(inputUsername);
    }
  };

  const handleScan = (detectedCodes: Array<{ rawValue: string }>) => {
    if (detectedCodes && detectedCodes.length > 0 && !loadingScan) {
      const scannedUsername = detectedCodes[0].rawValue;

      // Prevent duplicate scans of the same code
      if (scannedUsername === lastScannedCode) {
        return;
      }

      setLastScannedCode(scannedUsername);
      registerAttendance(scannedUsername);
    }
  };

  const handleStartScanning = async () => {
    if (!selectedEvent) {
      showAlert("error", "Du må velge et arrangement før du kan registrere oppmøte.");
      return;
    }
    setIsScanning(true);
  };

  const handleStopScanning = () => {
    setIsScanning(false);
  };

  const handleSwitchCamera = () => {
    if (availableCameras.length > 1) {
      // Stop scanning first
      setIsScanning(false);

      // Stop any existing video tracks immediately
      const video = document.querySelector("video");
      if (video && video.srcObject) {
        const stream = video.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }

      // Update camera index and switch key
      const nextIndex = (selectedCameraIndex + 1) % availableCameras.length;
      const nextCamera = availableCameras[nextIndex];

      // Determine if next camera is front camera
      const isNextFront = nextCamera.facingMode === "user" || (nextCamera.facingMode === undefined && nextIndex === 0);

      // Update state
      setSelectedCameraIndex(nextIndex);
      setIsFrontCamera(isNextFront);
      setCameraSwitchKey(prev => prev + 1); // Force remount

      // Wait a bit longer to ensure tracks are stopped before restarting
      setTimeout(() => {
        setIsScanning(true);
      }, 250);
    }
  };

  const drawBoundingBox = (
    detectedCodes: Array<{
      boundingBox: { x: number; y: number; width: number; height: number };
      cornerPoints: Array<{ x: number; y: number }>;
    }>,
    ctx: CanvasRenderingContext2D
  ) => {
    detectedCodes.forEach(detectedCode => {
      const { boundingBox } = detectedCode;

      // Draw bounding box
      ctx.strokeStyle = "var(--primary-color)";
      ctx.lineWidth = 2;
      ctx.strokeRect(boundingBox.x, boundingBox.y, boundingBox.width, boundingBox.height);
    });
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        padding: "20px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "800px",
          textAlign: "center",
        }}
      >
        <Title level={2} style={{ textAlign: "center", marginBottom: "8px" }}>
          Registrer Oppmøte
        </Title>

        {/* Event Selection + Start/Stop */}
        <div style={{ width: "100%", marginBottom: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "8px", gap: "8px", width: "100%" }}>
            <Select
              placeholder="Velg et arrangement"
              style={{ flex: 1, width: "100%" }}
              onChange={value => setSelectedEvent(Number(value))}
              value={selectedEvent ?? undefined}
              disabled={!!id}
            >
              {events.map(event => (
                <Option key={event.id} value={event.id}>
                  {event.name} - {new Date(event.start).toLocaleString()}
                </Option>
              ))}
            </Select>
            <Tooltip
              title={
                id
                  ? "Siden du har valgt et bestemt arrangement, vises kun dette arrangementet i listen. Åpne innslippsappen via verktøy i sidemenyen for å velge et annet arrangement."
                  : "I denne listen vises kun arrangementer som er stengt og som skjer i dag eller senere."
              }
            >
              <QuestionCircleOutlined style={{ fontSize: "16px", flexShrink: 0 }} />
            </Tooltip>
          </div>

          {!isScanning ? (
            <Button onClick={handleStartScanning} block disabled={!selectedEvent}>
              Start scanning
            </Button>
          ) : (
            <Button onClick={handleStopScanning} block>
              Stopp scanning
            </Button>
          )}
        </div>

        {/* Camera Feed / Scanner */}
        {isScanning && (
          <div style={{ width: "100%", margin: "0 auto" }}>
            <div
              style={{
                position: "relative",
                width: "100%",
                maxWidth: "640px",
                aspectRatio: "4/3",
                margin: "0 auto",
                overflow: "hidden",
                borderRadius: "8px",
              }}
            >
              {/* Camera switch button */}
              {availableCameras.length > 1 && (
                <Button
                  type="primary"
                  icon={<SwapOutlined />}
                  onClick={handleSwitchCamera}
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    zIndex: 1000,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                />
              )}

              <div
                style={{
                  width: "100%",
                  height: "100%",
                  transform: isFrontCamera ? "scaleX(-1)" : "none",
                }}
              >
                <Scanner
                  onScan={handleScan}
                  key={`camera-${selectedCameraIndex}-${cameraSwitchKey}-${isScanning}`}
                  components={{
                    torch: true,
                    zoom: true,
                    finder: false,
                    onOff: false,
                    tracker: drawBoundingBox,
                  }}
                  styles={{
                    container: {
                      width: "100%",
                      height: "100%",
                      position: "relative",
                    },
                    video: {
                      width: "100%",
                      height: "100%",
                      objectFit: "cover" as const,
                      objectPosition: "center",
                    },
                  }}
                  scanDelay={500}
                  allowMultiple={false}
                  paused={!isScanning || loadingScan}
                />
              </div>

              {/* Custom Finder Overlay */}
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "60%",
                  aspectRatio: "1/1",
                  pointerEvents: "none",
                  zIndex: 10,
                }}
              >
                {(() => {
                  const color =
                    finderColor === "var(--primary-color)"
                      ? getComputedStyle(document.documentElement).getPropertyValue("--primary-color").trim() ||
                        "#0a283f"
                      : finderColor;

                  return (
                    <svg
                      width="100%"
                      height="100%"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                      }}
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                    >
                      {/* Dashed border - more and tighter dashes */}
                      <rect
                        x="0"
                        y="0"
                        width="100"
                        height="100"
                        fill="none"
                        stroke={color}
                        strokeWidth="0.5"
                        strokeDasharray="1,1"
                      />
                      {/* Corner markers - thicker and longer */}
                      <g stroke={color} strokeWidth="3" fill="none" strokeLinecap="round">
                        {/* Top-left corner */}
                        <line x1="0" y1="0" x2="12" y2="0" />
                        <line x1="0" y1="0" x2="0" y2="12" />
                        {/* Top-right corner */}
                        <line x1="100" y1="0" x2="88" y2="0" />
                        <line x1="100" y1="0" x2="100" y2="12" />
                        {/* Bottom-left corner */}
                        <line x1="0" y1="100" x2="12" y2="100" />
                        <line x1="0" y1="100" x2="0" y2="88" />
                        {/* Bottom-right corner */}
                        <line x1="100" y1="100" x2="88" y2="100" />
                        <line x1="100" y1="100" x2="100" y2="88" />
                      </g>
                    </svg>
                  );
                })()}
              </div>

              {/* Loading overlay */}
              {loadingScan && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1000,
                    background: "rgba(0,0,0,0.7)",
                    padding: "20px",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Spin indicator={<LoadingOutlined style={{ color: "white" }} />} size="large" />
                  <Text style={{ color: "white" }}>Registrerer oppmøte...</Text>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Alert */}
        {alertInfo && isAlertVisible && (
          <div style={{ width: "100%", margin: "8px 0" }}>
            <Alert
              type={alertInfo.type}
              message={renderMessage(alertInfo.message)}
              showIcon
              closable
              onClose={() => setIsAlertVisible(false)}
            />
          </div>
        )}

        {/* Attendance Overview, with Manual Username Entry above the student lists */}
        <AttendanceOverview ref={attendanceRef} eventId={selectedEvent}>
          <ManualAttendanceInput
            disabled={!selectedEvent}
            onRegister={registerAttendance}
            onEmptyUsername={() => showAlert("error", "Skriv inn brukernavn før du registrerer oppmøte.")}
          />
        </AttendanceOverview>
      </div>
    </div>
  );
};

export default TakeAttendanceForEvent;
